import { useMaskito } from '@maskito/react';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { FormType } from '~/pages/pay/ui/PayPage.scheme';

import { useSteamCalculate } from '../hooks/useSteamCalculate';
import { decimalMaskOptions } from '../ui/SteamPage/SteamPage.mask';
import { withMaskitoRegister } from '../utils/withMaskitoRegister';

type PromocodeStatus = 'idle' | 'applied' | 'cancelledByEdit';

export const useSteamPageModel = () => {
  const {
    control,
    register,
    setValue,
    setError,
    getValues,
    formState: { errors, isValid, touchedFields },
  } = useFormContext<FormType>();

  const hasPromocodeError = Boolean(errors.promocode);
  const hasNonPromocodeErrors = Object.keys(errors).some(
    (fieldName) => fieldName !== 'promocode',
  );
  const isSubmitValid =
    isValid || (hasPromocodeError && !hasNonPromocodeErrors);

  const certificate = getValues('certificate');
  const productId = certificate?.products?.[0]?.id;
  const amount = useWatch({ control, name: 'steamData.amount' });

  const [initialPromocode] = useState(() =>
    (getValues('promocode') || '').toUpperCase(),
  );

  const [inputValue, setInputValue] = useState(initialPromocode);
  const [appliedPromocode, setAppliedPromocode] = useState<string | undefined>(
    initialPromocode || undefined,
  );
  const [status, setStatus] = useState<PromocodeStatus>(
    initialPromocode ? 'applied' : 'idle',
  );
  const [shouldRefetchWithoutPromocode, setShouldRefetchWithoutPromocode] =
    useState(false);

  const {
    amounts,
    isCalculating,
    isApplyingPromocode,
    applyPromocodeMutation,
    refetch,
  } = useSteamCalculate({
    productId,
    amount,
    appliedPromocode,
    setError,
  });

  const setPromocodeInput = useCallback(
    (nextValue: string) => {
      setInputValue(nextValue);

      if (status === 'applied' && nextValue !== appliedPromocode) {
        setStatus(nextValue ? 'cancelledByEdit' : 'idle');
        setAppliedPromocode(undefined);
        setValue('promocode', undefined, { shouldValidate: true });
        setShouldRefetchWithoutPromocode(true);
        return;
      }

      if (status === 'cancelledByEdit' && !nextValue) {
        setStatus('idle');
      }
    },
    [appliedPromocode, setValue, status],
  );

  const applyPromocode = useCallback(
    (code: string) => {
      if (!code) {
        return;
      }

      if (status === 'cancelledByEdit') {
        setStatus('idle');
      }

      applyPromocodeMutation.mutate(code, {
        onSuccess: () => {
          setAppliedPromocode(code);
          setInputValue(code);
          setStatus('applied');
          setValue('promocode', code, { shouldValidate: true });
        },
      });
    },
    [applyPromocodeMutation, setValue, status],
  );

  const resetPromocode = useCallback(() => {
    const wasActive = status === 'applied' || status === 'cancelledByEdit';

    setInputValue('');
    setAppliedPromocode(undefined);
    setStatus('idle');
    setValue('promocode', undefined, { shouldValidate: true });
    setError('promocode', { message: undefined });

    if (wasActive) {
      setShouldRefetchWithoutPromocode(true);
    }
  }, [setError, setValue, status]);

  useEffect(() => {
    if (!shouldRefetchWithoutPromocode || appliedPromocode) {
      return;
    }

    void refetch();
    setShouldRefetchWithoutPromocode(false);
  }, [appliedPromocode, refetch, shouldRefetchWithoutPromocode]);

  const amountMaskRef = useMaskito({ options: decimalMaskOptions });
  const registerAmount = withMaskitoRegister(
    register('steamData.amount', { valueAsNumber: true }),
    amountMaskRef,
  );

  const getErrorMessage = useCallback(
    (field: keyof NonNullable<FormType['steamData']>) => {
      const isTouched = touchedFields.steamData?.[field];

      return isTouched && errors?.steamData?.[field]?.message
        ? (errors.steamData[field]?.message as string)
        : undefined;
    },
    [errors.steamData, touchedFields.steamData],
  );

  return {
    form: {
      register,
      control,
      isSubmitValid,
      registerAmount,
      getErrorMessage,
      setValue,
    },
    data: {
      certificate,
      amounts,
      isCalculating,
    },
    promocode: {
      inputValue,
      status,
      isApplying: isApplyingPromocode,
      handlers: {
        setInputValue: setPromocodeInput,
        startApply: applyPromocode,
        reset: resetPromocode,
      },
    },
  };
};
